import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BusinessProfileModal — edit the invoice issuer identity & numbering.
 * Admin-only. English UI. The values here are snapshotted onto each invoice
 * at generation time (immutable), so editing does NOT alter past invoices.
 */
const FIELDS = [
  { key: 'name', label: 'Business name' },
  { key: 'owner_name', label: 'Owner / trainer name' },
  { key: 'nif', label: 'NIF / CIF' },
  { key: 'address_line', label: 'Address line' },
  { key: 'city_line', label: 'City / postal / province' },
  { key: 'iban', label: 'IBAN' },
  { key: 'payment_method', label: 'Payment method (default)' },
  { key: 'email', label: 'Email (optional)' },
  { key: 'phone', label: 'Phone (optional)' },
];

const BusinessProfileModal = ({ onClose }) => {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invoices/business-profile`).then((r) => setProfile(r.data)).catch(() => setProfile({}));
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...profile, vat_rate: parseFloat(profile.vat_rate) || 21 };
      delete payload.id;
      await axios.put(`${API}/invoices/business-profile`, payload);
      onClose();
    } catch (e) {
      alert('Error saving: ' + (e.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="business-profile-modal">
      <div className="bg-blue-900 rounded-xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-bold text-xl mb-1">Invoice / Business Settings</h3>
        <p className="text-blue-300 text-sm mb-5">Issuer identity used on every invoice. Editing does not change already-generated invoices.</p>

        <div className="grid grid-cols-1 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-blue-200 text-xs mb-1">{f.label}</label>
              <input
                type="text"
                value={profile[f.key] || ''}
                onChange={(e) => set(f.key, e.target.value)}
                className="input-field w-full"
                data-testid={`biz-${f.key}`}
              />
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-blue-200 text-xs mb-1">Invoice number prefix</label>
              <input
                type="text"
                value={profile.invoice_prefix || ''}
                onChange={(e) => set('invoice_prefix', e.target.value)}
                className="input-field w-full"
                placeholder="e.g. BTC-"
                data-testid="biz-invoice_prefix"
              />
              <p className="text-blue-400 text-[11px] mt-1">
                Preview: <span className="font-mono text-blue-200">{(profile.invoice_prefix || '')}{new Date().getFullYear()}-001</span>
              </p>
            </div>
            <div>
              <label className="block text-blue-200 text-xs mb-1">VAT rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={profile.vat_rate ?? 21}
                onChange={(e) => set('vat_rate', e.target.value)}
                className="input-field w-full"
                data-testid="biz-vat_rate"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="biz-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="biz-save">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { Settings };
export default BusinessProfileModal;
